// Searchable person picker for spending limits: who gets their own limit, and
// who joins a budget group. Members first, then guests, each guest with a
// second line that tells them apart (lib/spend-limits.ts guestDetail).

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { SpendDirectoryEntry } from "@/lib/api";
import { guestDetail, personMatches, personSearchText, ROLE_WORD } from "@/lib/spend-limits";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useRef, useState } from "react";

function detailOf(p: SpendDirectoryEntry, groupName?: (id: string | null) => string | null): string | null {
  const parts = [guestDetail(p) ?? p.email, p.role ? ROLE_WORD[p.role] : null, groupName?.(p.groupId) ?? null];
  return parts.filter(Boolean).join(" · ") || null;
}

export function SpendPersonPicker({
  id,
  people,
  value,
  onChange,
  placeholder = "Choose a person…",
  ariaLabel,
  ariaDescribedBy,
  defaultOpen = false,
  focusAfterPick,
  groupName,
  disabled,
  className,
}: {
  id?: string;
  people: SpendDirectoryEntry[];
  /** The picked person key, or "" for none. */
  value: string;
  onChange: (personKey: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  /** Open with the search focused as soon as it mounts. */
  defaultOpen?: boolean;
  /** Id of the field that takes focus after a pick, instead of the trigger. */
  focusAfterPick?: string;
  /** Adds each person's current budget group to their second line. */
  groupName?: (groupId: string | null) => string | null;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const picked = useRef(false);
  const selected = people.find((p) => p.personKey === value);
  const members = people.filter((p) => !p.isGuest);
  const guests = people.filter((p) => p.isGuest);

  const pick = (personKey: string) => {
    picked.current = true;
    onChange(personKey);
    setOpen(false);
  };

  const item = (p: SpendDirectoryEntry) => {
    const detail = detailOf(p, groupName);
    return (
      <CommandItem key={p.personKey} value={p.personKey} keywords={[personSearchText(p)]} onSelect={() => pick(p.personKey)} className="gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate">{p.name}</div>
          {detail && <div className="truncate text-xs text-muted-foreground">{detail}</div>}
        </div>
        <Check className={cn("size-4 shrink-0", value === p.personKey ? "opacity-100" : "opacity-0")} />
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className={cn(
            "h-8 w-full justify-between gap-2 rounded-lg border-input bg-background px-2.5 font-normal dark:bg-input/30",
            className,
          )}
        >
          {selected ? (
            <span className="truncate">
              {selected.name}
              {selected.isGuest && guestDetail(selected) && (
                <span className="text-muted-foreground"> · {guestDetail(selected)}</span>
              )}
            </span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(22rem,calc(100vw-2rem))] p-0"
        align="start"
        onCloseAutoFocus={(e) => {
          const next = picked.current && focusAfterPick ? document.getElementById(focusAfterPick) : null;
          picked.current = false;
          if (next) {
            e.preventDefault();
            next.focus();
          }
        }}
      >
        <Command filter={(_value, search, keywords) => personMatches(keywords?.join(" ") ?? "", search)}>
          <CommandInput placeholder="Search by name, email or chat app" />
          <CommandList>
            <CommandEmpty>No one matches.</CommandEmpty>
            {members.length > 0 && <CommandGroup heading="People in your organization">{members.map(item)}</CommandGroup>}
            {guests.length > 0 && <CommandGroup heading="Guests">{guests.map(item)}</CommandGroup>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
